import { GridAdapter } from "./adapters/GridAdapter";
import { GridInstance } from "./models/GridInstance";

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * Manages the SVG overlay that sits on top of a target element.
 *
 * Responsibilities:
 * - Creates and positions the SVG element
 * - Delegates rendering to the appropriate GridAdapter per instance
 * - Handles resize/reposition via `refresh()`
 */
export class GridOverlay {
  private svg: SVGSVGElement;
  private target: HTMLElement;
  private resizeObserver: ResizeObserver;
  private enabled: boolean = true;

  constructor(target: HTMLElement, world: HTMLElement) {
    this.target = target;
    this.svg = document.createElementNS(SVG_NS, "svg") as SVGSVGElement;
    this.svg.setAttribute("data-string-grid-overlay", "");
    this.svg.style.position = "absolute";
    this.svg.style.pointerEvents = "none";
    this.svg.style.transformOrigin = "top left";

    world.appendChild(this.svg);
    this.syncViewBox();

    this.resizeObserver = new ResizeObserver(() => this.syncViewBox());
    this.resizeObserver.observe(this.target);
  }

  public getElement(): SVGSVGElement { return this.svg; }

  public setPosition(docX: number, docY: number, width: number, height: number): void {
    this.svg.style.width = `${width}px`;
    this.svg.style.height = `${height}px`;
    this.svg.style.transform = `translate3d(${Math.round(docX * 100) / 100}px, ${Math.round(docY * 100) / 100}px, 0)`;
  }

  /**
   * Re-renders all visible grid instances.
   * Clears the entire SVG first so stale groups from a previous layout are removed.
   */
  render(instances: GridInstance[], adapterRegistry: Map<string, GridAdapter>): void {
    this.clearAll();
    if (!this.enabled) {
      return;
    }
    const { width, height } = this.getDimensions();

    for (const instance of [...instances].reverse()) {
      const adapter = adapterRegistry.get(instance.type);
      if (!adapter) continue;

      if (instance.visible) {
        adapter.render(this.svg, width, height, {
          ...instance.settings,
          __instanceId: instance.id,
        });
        const group = this.svg.querySelector(
          `[data-grid-id="${instance.id}"]`,
        ) as SVGGElement | null;
        if (group) {
          group.setAttribute("data-grid-type", instance.type);
          group.setAttribute("data-grid-visible", "true");
        }
      }
    }
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.svg.style.display = enabled ? "" : "none";
    if (!enabled) {
      this.clearAll();
    }
  }

  /**
   * Removes all rendered grid groups from the SVG.
   */
  clearAll(): void {
    const groups = this.svg.querySelectorAll("[data-grid-id]");
    for (let i = groups.length - 1; i >= 0; i--) {
      groups[i].remove();
    }
  }

  /**
   * Clears a specific grid instance from the overlay.
   */
  clearInstance(instanceId: string, adapter: GridAdapter): void {
    adapter.clear(this.svg, instanceId);
  }

  /**
   * Syncs the SVG viewBox to match element dimensions.
   */
  syncViewBox(): void {
    const { width, height } = this.getDimensions();
    this.svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
  }

  /**
   * Returns the current dimensions of the target element.
   */
  getDimensions(): { width: number; height: number } {
    return {
      width: this.target.offsetWidth,
      height: this.target.offsetHeight,
    };
  }

  /**
   * Cleans up the overlay and observer.
   */
  destroy(): void {
    this.resizeObserver.disconnect();
    this.svg.remove();
  }

  /**
   * Ensures the target element has positioning context for the overlay.
   */
  private ensurePositioned(): void {
    const computed = getComputedStyle(this.target);
    if (computed.position === "static") {
      this.target.style.position = "relative";
    }
  }
}
